import { Text } from "@blueprintjs/core"
import CustomTag from "components/Atoms/CustomTag"
import StyledLink from "components/Atoms/StyledLink"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import SerialColumn from "components/Organisms/table/SerialColumn"
import CustomReactTable from "components/Templates/CustomReactTable"
import {
  useModuleDcs,
  useOrdersBySellableItem,
  useOrdersBySellableItemSummary,
} from "graphql/order/order.hooks"
import React, { useMemo } from "react"

const SellableItemOrders = () => {
  const { data, loading } = useOrdersBySellableItem()
  const { data: sData } = useOrdersBySellableItemSummary()
  const summary = sData?.summaryAllOrders
  const items = data?.ordersBySellableItem

  const COLUMNS = useMemo(
    () => [
      SerialColumn,
      {
        maxWidth: 200,
        Header: "Sellable Item",
        className: "text-left",
        accessor: "sellableItem",
        Cell({ value: item }) {
          const photo = item?.primaryPhoto
          return (
            <div>
              {photo?.id && (
                <img
                  className="mr-2"
                  src={photo?.url}
                  alt={item?.name}
                  width={16}
                  height={16}
                />
              )}
              <StyledLink to={`/d/registration/sellable-items/${item?.id}`}>
                {item?.name}
              </StyledLink>
              <Text ellipsize={true} className="text-xs vt-muted">
                {item?.shortDescription}
              </Text>
            </div>
          )
        },
      },
      {
        maxWidth: 250,
        Header: "Varieties",
        accessor: "sellableItem.sellableVarieties",
        Cell({ value }) {
          return <OverFlowMenu items={value} />
        },
      },
      {
        Header() {
          const dcs = useModuleDcs("orders")
          return (
            <span>
              Orders Summary{" "}
              {dcs?.map(dc => (
                <CustomTag className="mr-2" key={dc?.id} intent="success">
                  {dc?.name}
                </CustomTag>
              ))}
            </span>
          )
        },
        Footer() {
          return (
            <span>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-2">
                  <span>
                    Total Items:{" "}
                    <span className="font-bold">{summary?.itemCount || 0}</span>
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="ml-3">
                    Confirmed:{" "}
                    <span className="font-bold vt-primary">
                      {summary?.itemStatusConfirmed || 0}
                    </span>
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="ml-3">
                    Cancelled:{" "}
                    <span className="font-bold vt-danger">
                      {summary?.itemStatusCancelledByUser || 0}
                    </span>
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="ml-3">
                    At Production:{" "}
                    <span className="font-bold vt-success">
                      {summary?.itemStatusInProcess || 0}
                    </span>
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="ml-3">
                    Ready For Dispatch:{" "}
                    <span className="font-bold vt-warning">
                      {summary?.itemStatusReadyForDespatch || 0}
                    </span>
                  </span>
                </div>
              </div>
            </span>
          )
        },
        accessor: "statusDetails",
        Cell({ value }) {
          const total = value?.reduce((p, i) => p + i.quantity, 0)
          const map = value?.reduce(
            (p, i) => ({ ...p, [i?.orderStatus]: i }),
            {}
          )

          return (
            <span>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-2">
                  <span>
                    Total Items: <span className="font-bold">{total}</span>
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="ml-3">
                    Confirmed:{" "}
                    <span className="font-bold vt-primary">
                      {map?.CNF?.quantity ?? 0}
                    </span>
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="ml-3">
                    Cancelled:{" "}
                    <span className="font-bold vt-danger">
                      {map?.CBU?.quantity ?? 0}
                    </span>
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="ml-3">
                    At Production:{" "}
                    <span className="font-bold vt-success">
                      {map?.IPR?.quantity ?? 0}
                    </span>
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="ml-3">
                    Ready For Dispatch:{" "}
                    <span className="font-bold vt-warning">
                      {map?.RFD?.quantity ?? 0}
                    </span>
                  </span>
                </div>
              </div>
            </span>
          )
        },
      },
    ],
    [summary]
  )
  return (
    <>
      <CustomReactTable
        columns={COLUMNS}
        data={items}
        loading={loading}
        loadingText="Fetching Items"
        pageSize={items?.length > 15 ? items?.length : 15}
      />
    </>
  )
}

export default SellableItemOrders
